/**
 * Vault Debug Utilities
 * 
 * Exposed on window for localhost or specific user IDs.
 * These tools help validate the vault pipeline and troubleshoot issues.
 */

import vaultService from '../services/vaultService';
import vaultWriterService from '../services/vaultWriterService';
import { supabase } from '../lib/supabase';

const ALLOWED_USER_IDS = [
  'f63f401e-d3ea-4dd0-90e7-9eaa3efcfe7d', // Bryce's user ID
];

/**
 * Check if debug tools should be exposed
 */
function shouldExposeDebugTools() {
  // Always expose on localhost
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return true;
  }
  
  // Expose for specific user IDs
  // (Check after user is loaded)
  return false;
}

/**
 * Get current user ID
 */
async function getCurrentUserId() {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id || null;
}

/**
 * 1. BACKFILL VAULT
 * 
 * Scans deepsearch.items and fills missing enrichment data
 */
export async function backfillVault(userId = null) {
  if (import.meta.env.DEV) {
    console.log('');
    console.log('═══════════════════════════════════════════');
    console.log('🔄 BACKFILL VAULT');
    console.log('═══════════════════════════════════════════');
    console.log('');
  }
  
  if (!userId) {
    userId = await getCurrentUserId();
  }
  
  if (!userId) {
    console.error('❌ No user ID provided');
    return;
  }
  
  if (import.meta.env.DEV) {
    console.log('User ID:', userId.slice(0, 8));
    console.log('');
  }
  
  try {
    // Get all items
    const allItems = await vaultService.getAllItems(userId, { forceRefresh: true });
    if (import.meta.env.DEV) console.log('📊 Total items:', allItems.length);
    
    // Find items missing enrichment
    const needsEnrichment = allItems.filter(item => {
      const missingEmbedding = !item.embedding;
      const missingThemes = !item.extractedThemes || item.extractedThemes.length === 0;
      const missingTopics = !item.microTopics || item.microTopics.length === 0;
      const missingChannel = !item.channel || item.channel === 'Unknown';
      const missingAnalysis = !item.aiAnalysis || !item.aiAnalysis.summary;
      
      return missingEmbedding || missingThemes || missingTopics || missingChannel || missingAnalysis;
    });
    
    if (import.meta.env.DEV) console.log('📊 Items needing enrichment:', needsEnrichment.length);
    
    if (needsEnrichment.length === 0) {
      if (import.meta.env.DEV) {
        console.log('✅ All items are fully enriched!');
        console.log('');
      }
      return { enriched: 0, errors: [], skipped: 0 };
    }
    
    // Show breakdown
    const breakdown = {
      missingEmbedding: 0,
      missingThemes: 0,
      missingTopics: 0,
      missingChannel: 0,
      missingAnalysis: 0,
    };
    
    needsEnrichment.forEach(item => {
      if (!item.embedding) breakdown.missingEmbedding++;
      if (!item.extractedThemes || item.extractedThemes.length === 0) breakdown.missingThemes++;
      if (!item.microTopics || item.microTopics.length === 0) breakdown.missingTopics++;
      if (!item.channel || item.channel === 'Unknown') breakdown.missingChannel++;
      if (!item.aiAnalysis || !item.aiAnalysis.summary) breakdown.missingAnalysis++;
    });
    
    if (import.meta.env.DEV) {
      console.log('📊 Breakdown:');
      console.log('   Missing embeddings:', breakdown.missingEmbedding);
      console.log('   Missing themes:', breakdown.missingThemes);
      console.log('   Missing topics:', breakdown.missingTopics);
      console.log('   Missing/bad channel:', breakdown.missingChannel);
      console.log('   Missing AI analysis:', breakdown.missingAnalysis);
      console.log('');
    }
    
    // Backfill (limit to 50 at a time)
    const batchSize = 50;
    const batch = needsEnrichment.slice(0, batchSize);
    
    if (import.meta.env.DEV) {
      console.log(`🚀 Enriching ${batch.length} items...`);
      console.log('');
    }
    
    const results = [];
    const errors = [];
    const skipped = [];
    
    for (let i = 0; i < batch.length; i++) {
      const item = batch[i];
      
      try {
        // VALIDATION BEFORE ENRICHING
        // Skip malformed items that can't be enriched
        const isMalformed = !item.title && !item.description && !item.url && !item.textContent;
        
        if (isMalformed) {
          if (import.meta.env.DEV) console.warn(`   ⚠️  [${i + 1}/${batch.length}] Skipped malformed item: ${item.id}`);
          skipped.push(item.id);
          continue;
        }
        
        if (import.meta.env.DEV) console.log(`   📝 [${i + 1}/${batch.length}] Enriching: ${item.title?.substring(0, 40) || item.id}...`);
        
        // ALWAYS use enrichAndUpsertItem (soft-fails internally)
        const enriched = await vaultWriterService.enrichAndUpsertItem(item);
        
        if (enriched) {
          if (import.meta.env.DEV) console.log(`   ✅ [${i + 1}/${batch.length}] Success`);
          results.push(item.id);
        } else {
          if (import.meta.env.DEV) console.warn(`   ⚠️  [${i + 1}/${batch.length}] Enrichment returned null`);
          skipped.push(item.id);
        }
        
      } catch (error) {
        // FULL try/catch: One bad item NEVER stops the batch
        if (import.meta.env.DEV) console.warn(`   ❌ [${i + 1}/${batch.length}] Error:`, error.message);
        errors.push({ itemId: item.id, error: error.message });
        // Continue processing next item
      }
    }
    
    if (import.meta.env.DEV) {
      console.log('');
      console.log('═══════════════════════════════════════════');
      console.log('✅ BACKFILL COMPLETE');
      console.log('═══════════════════════════════════════════');
      console.log('   Enriched:', results.length);
      console.log('   Skipped:', skipped.length);
      console.log('   Errors:', errors.length);
      console.log('   Remaining:', Math.max(0, needsEnrichment.length - batchSize));
      console.log('');
      
      if (skipped.length > 0) {
        console.log('⚠️  Skipped items:');
        skipped.forEach(id => console.log(`   - ${id}`));
        console.log('');
      }
      
      if (errors.length > 0) {
        console.log('❌ Errors:');
        errors.forEach(e => console.log(`   - ${e.itemId}: ${e.error}`));
        console.log('');
      }
      
      if (results.length > 0) {
        console.log('💡 TIP: Run window.validateVault() to verify enrichment');
        console.log('');
      }
    }
    
    return {
      enriched: results.length,
      skipped: skipped.length,
      errors,
      remaining: Math.max(0, needsEnrichment.length - batchSize),
    };
    
  } catch (error) {
    console.error('❌ Backfill failed:', error);
    throw error;
  }
}

/**
 * 2. VALIDATE VAULT
 * 
 * Returns counts of items with missing data
 */
export async function validateVault(userId = null) {
  if (import.meta.env.DEV) {
    console.log('');
    console.log('═══════════════════════════════════════════');
    console.log('🔍 VALIDATE VAULT');
    console.log('═══════════════════════════════════════════');
    console.log('');
  }
  
  if (!userId) {
    userId = await getCurrentUserId();
  }
  
  if (!userId) {
    console.error('❌ No user ID provided');
    return;
  }
  
  if (import.meta.env.DEV) {
    console.log('User ID:', userId.slice(0, 8));
    console.log('');
  }
  
  try {
    // Get all items (SAFE - never throws)
    let allItems = [];
    try {
      allItems = await vaultService.getAllItems(userId, { forceRefresh: true });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('⚠️  Error loading items:', error.message);
        console.log('Continuing with partial data...');
        console.log('');
      }
    }
    
    if (import.meta.env.DEV) {
      console.log('📊 VAULT STATISTICS');
      console.log('═══════════════════════════════════════════');
      console.log('Total items:', allItems.length);
      console.log('');
    }
    
    // Count by source
    const bySource = {};
    allItems.forEach(item => {
      const source = item.originalSourceTable || 'unknown';
      bySource[source] = (bySource[source] || 0) + 1;
    });
    
    if (import.meta.env.DEV) {
      console.log('📊 Items by source:');
      Object.entries(bySource).forEach(([source, count]) => {
        console.log(`   ${source}: ${count}`);
      });
      console.log('');
    }
    
    // Count missing enrichment (SAFE - handles partially enriched items)
    const validation = {
      total: allItems.length,
      missingEmbedding: 0,
      missingThemes: 0,
      missingTopics: 0,
      missingChannel: 0,
      missingAnalysis: 0,
      fullyEnriched: 0,
      partiallyEnriched: 0,
      malformed: [],
    };
    
    allItems.forEach(item => {
      try {
        // SAFE checks - handle missing/null/undefined fields
        const hasEmbedding = Array.isArray(item.embedding) && item.embedding.length > 0;
        const hasThemes = Array.isArray(item.extractedThemes) && item.extractedThemes.length > 0;
        const hasTopics = Array.isArray(item.microTopics) && item.microTopics.length > 0;
        const hasChannel = !!item.channel && item.channel !== 'Unknown';
        const hasAnalysis = !!item.aiAnalysis?.summary && item.aiAnalysis.summary.length >= 20;
        
        // Count missing
        if (!hasEmbedding) validation.missingEmbedding++;
        if (!hasThemes) validation.missingThemes++;
        if (!hasTopics) validation.missingTopics++;
        if (!hasChannel) validation.missingChannel++;
        if (!hasAnalysis) validation.missingAnalysis++;
        
        // Check if malformed (can't be enriched)
        const isMalformed = !item.title && !item.description && !item.url && !item.textContent;
        if (isMalformed) {
          validation.malformed.push(item.id);
        }
        
        // Count enrichment level
        const enrichmentCount = [hasEmbedding, hasThemes, hasTopics, hasChannel, hasAnalysis].filter(Boolean).length;
        
        if (enrichmentCount === 5) {
          validation.fullyEnriched++;
        } else if (enrichmentCount > 0) {
          validation.partiallyEnriched++;
        }
        
      } catch (itemError) {
        // If individual item validation fails, count as missing and continue
        if (import.meta.env.DEV) console.warn(`⚠️  Error validating item ${item?.id}:`, itemError.message);
        validation.missingThemes++;
        validation.missingTopics++;
        validation.missingEmbedding++;
      }
    });
    
    // SAFE percentage calculation
    const enrichmentRate = validation.total > 0
      ? (validation.fullyEnriched / validation.total * 100).toFixed(1)
      : 0;
    
    if (import.meta.env.DEV) {
      console.log('📊 ENRICHMENT STATUS');
      console.log('═══════════════════════════════════════════');
      console.log('Fully enriched:', validation.fullyEnriched, `(${enrichmentRate}%)`);
      console.log('Partially enriched:', validation.partiallyEnriched);
      console.log('Malformed (unenrichable):', validation.malformed.length);
      console.log('');
      console.log('Missing data:');
      console.log('   Embeddings:', validation.missingEmbedding);
      console.log('   Themes:', validation.missingThemes);
      console.log('   Topics:', validation.missingTopics);
      console.log('   Channel:', validation.missingChannel);
      console.log('   AI Analysis:', validation.missingAnalysis);
      console.log('');
      
      // Show validation result
      if (validation.fullyEnriched === validation.total && validation.total > 0) {
        console.log('✅✅✅ ALL ITEMS FULLY ENRICHED ✅✅✅');
      } else if (validation.total === 0) {
        console.log('⚠️  NO ITEMS FOUND - Add content to Swirl first');
      } else {
        const needsEnrichment = validation.total - validation.fullyEnriched - validation.malformed.length;
        console.log('⚠️  VALIDATION INCOMPLETE');
        console.log(`${needsEnrichment} items need enrichment`);
        console.log('');
        console.log('💡 Run: window.backfillVault()');
      }
      
      console.log('');
      console.log('═══════════════════════════════════════════');
      console.log('');
    }
    
    return validation;
    
  } catch (error) {
    // SAFE handling - log error but don't crash
    console.error('❌ Validation failed:', error.message);
    if (import.meta.env.DEV) {
      console.log('');
      console.log('Returning partial stats...');
    }
    return {
      total: 0,
      fullyEnriched: 0,
      error: error.message,
    };
  }
}

/**
 * 3. TEST VAULT PIPELINE
 * 
 * Creates a test item, saves it, reads it back
 */
export async function testVaultPipeline(userId = null) {
  if (import.meta.env.DEV) {
    console.log('');
    console.log('═══════════════════════════════════════════');
    console.log('🧪 TEST VAULT PIPELINE');
    console.log('═══════════════════════════════════════════');
    console.log('');
  }
  
  if (!userId) {
    userId = await getCurrentUserId();
  }
  
  if (!userId) {
    console.error('❌ No user ID provided');
    return;
  }
  
  if (import.meta.env.DEV) {
    console.log('User ID:', userId.slice(0, 8));
    console.log('');
  }
  
  try {
    // Create test item
    const testItem = {
      user_id: userId,
      title: `Test Item ${Date.now()}`,
      description: 'This is a test item for vault pipeline validation',
      url: `https://example.com/test/${Date.now()}`,
      content: 'Test content for enrichment. This should generate embeddings and extract topics like testing, validation, and pipeline quality checks.',
    };
    
    if (import.meta.env.DEV) {
      console.log('📝 Creating test item...');
      console.log('   Title:', testItem.title);
      console.log('');
    }
    
    // Save via vault writer
    if (import.meta.env.DEV) console.log('💾 Saving via vaultWriterService...');
    const saved = await vaultWriterService.saveItem(testItem, {
      sourceTable: 'test',
      skipEnrichment: false,
      skipEmbedding: false,
    });
    
    if (import.meta.env.DEV) {
      console.log('✅ Item saved:', saved.id);
      console.log('');
    }
    
    // Read back via vault service
    if (import.meta.env.DEV) console.log('📖 Reading back via vaultService...');
    const allItems = await vaultService.getAllItems(userId, { forceRefresh: true });
    const retrieved = allItems.find(i => i.id === saved.id);
    
    if (!retrieved) {
      console.error('❌ Item not found in vault!');
      return { success: false, error: 'Item not found' };
    }
    
    if (import.meta.env.DEV) {
      console.log('✅ Item retrieved from vault');
      console.log('');
      
      // Validate enrichment
      console.log('📊 ENRICHMENT CHECK');
      console.log('═══════════════════════════════════════════');
      console.log('   Title:', retrieved.title);
      console.log('   Themes:', retrieved.extractedThemes);
      console.log('   Topics:', retrieved.microTopics);
      console.log('   Entities:', retrieved.entities);
      console.log('   Channel:', retrieved.channel);
      console.log('   Has embedding:', !!retrieved.embedding, retrieved.embedding ? `(${retrieved.embedding.length} dims)` : '');
      console.log('   Source table:', retrieved.originalSourceTable);
      console.log('');
      
      if (retrieved.aiAnalysis) {
        console.log('   AI Analysis:');
        console.log('     Summary:', retrieved.aiAnalysis.summary?.substring(0, 100) + '...');
        console.log('     Why it matters:', retrieved.aiAnalysis.whyItMatters?.substring(0, 100) + '...');
        console.log('     Related:', retrieved.aiAnalysis.relatedConcepts);
      }
      console.log('');
      
      // COMPREHENSIVE VALIDATION
      console.log('📊 VALIDATION');
      console.log('═══════════════════════════════════════════');
    }
    
    const checks = {
      hasThemes: Array.isArray(retrieved.extractedThemes) && retrieved.extractedThemes.length > 0,
      hasTopics: Array.isArray(retrieved.microTopics) && retrieved.microTopics.length > 0,
      hasEntities: Array.isArray(retrieved.entities),
      hasSummary: retrieved.aiAnalysis?.summary && retrieved.aiAnalysis.summary.length >= 20,
      hasWhyItMatters: !!retrieved.aiAnalysis?.whyItMatters,
      hasRelatedConcepts: Array.isArray(retrieved.aiAnalysis?.relatedConcepts),
      hasEmbedding: Array.isArray(retrieved.embedding) && retrieved.embedding.length === 1536,
      hasChannel: retrieved.channel && retrieved.channel !== 'Unknown',
    };
    
    if (import.meta.env.DEV) {
      console.log('   ✓ Has themes (1-3):', checks.hasThemes ? '✅' : '❌', retrieved.extractedThemes);
      console.log('   ✓ Has topics (3-10):', checks.hasTopics ? '✅' : '❌', retrieved.microTopics?.length || 0);
      console.log('   ✓ Has entities:', checks.hasEntities ? '✅' : '❌', retrieved.entities?.length || 0);
      console.log('   ✓ Has AI summary:', checks.hasSummary ? '✅' : '❌');
      console.log('   ✓ Has whyItMatters:', checks.hasWhyItMatters ? '✅' : '❌');
      console.log('   ✓ Has relatedConcepts:', checks.hasRelatedConcepts ? '✅' : '❌', retrieved.aiAnalysis?.relatedConcepts?.length || 0);
      console.log('   ✓ Has embedding (1536d):', checks.hasEmbedding ? '✅' : '❌', retrieved.embedding?.length || 0);
      console.log('   ✓ Has channel:', checks.hasChannel ? '✅' : '❌', retrieved.channel);
      console.log('');
    }
    
    // All critical checks must pass
    const criticalChecks = [
      checks.hasThemes,
      checks.hasTopics,
      checks.hasSummary,
      checks.hasChannel,
    ];
    
    const isComplete = criticalChecks.every(check => check === true);
    
    if (import.meta.env.DEV) {
      if (isComplete) {
        console.log('✅✅✅ TEST PASSED - Item fully enriched ✅✅✅');
      } else {
        console.log('❌ TEST FAILED - Critical enrichment missing:');
        if (!checks.hasThemes) console.log('   ❌ Missing themes');
        if (!checks.hasTopics) console.log('   ❌ Missing topics');
        if (!checks.hasSummary) console.log('   ❌ Missing AI summary');
        if (!checks.hasChannel) console.log('   ❌ Missing channel');
      }
      
      console.log('');
      console.log('═══════════════════════════════════════════');
      console.log('');
    }
    
    return {
      success: isComplete,
      itemId: saved.id,
      enrichment: {
        themes: retrieved.extractedThemes,
        topics: retrieved.microTopics,
        entities: retrieved.entities,
        hasEmbedding: !!retrieved.embedding,
        hasAnalysis: !!retrieved.aiAnalysis?.summary,
        channel: retrieved.channel,
      },
    };
    
  } catch (error) {
    console.error('❌ Test failed:', error);
    throw error;
  }
}

/**
 * 4. INVALIDATE INSIGHTS CACHE
 * 
 * Clears all client-side caches for dashboard/insights
 */
export async function invalidateInsightsCache() {
  if (import.meta.env.DEV) {
    console.log('');
    console.log('═══════════════════════════════════════════');
    console.log('🗑️  INVALIDATE INSIGHTS CACHE');
    console.log('═══════════════════════════════════════════');
    console.log('');
  }
  
  const userId = await getCurrentUserId();
  if (!userId) {
    console.error('❌ No user logged in');
    return;
  }
  
  // Clear all cache keys
  const cacheKeys = [
    `suggestions_cache_${userId}`,
    `suggestions_timestamp_${userId}`,
    `memory_surfacing_v2_${userId}`,
    `memory_surfacing_v2_${userId}_timestamp`,
    `vault_items_${userId}`,
    `vault_items_${userId}_timestamp`,
  ];
  
  cacheKeys.forEach(key => {
    localStorage.removeItem(key);
    if (import.meta.env.DEV) console.log(`   ✓ Cleared: ${key}`);
  });
  
  if (import.meta.env.DEV) {
    console.log('');
    console.log('✅ All insight caches cleared');
    console.log('');
    console.log('═══════════════════════════════════════════');
    console.log('');
  }
}

/**
 * 5. FORCE INSIGHTS REFRESH
 * 
 * Refetches dashboard and insights data
 */
export async function forceInsightsRefresh() {
  if (import.meta.env.DEV) {
    console.log('');
    console.log('═══════════════════════════════════════════');
    console.log('🔄 FORCE INSIGHTS REFRESH');
    console.log('═══════════════════════════════════════════');
    console.log('');
  }
  
  const userId = await getCurrentUserId();
  if (!userId) {
    console.error('❌ No user logged in');
    return;
  }
  
  if (import.meta.env.DEV) {
    console.log('User ID:', userId.slice(0, 8));
    console.log('');
  }
  
  // Clear caches first
  await invalidateInsightsCache();
  
  // Force refresh vault
  if (import.meta.env.DEV) console.log('📖 Fetching fresh vault data...');
  const items = await vaultService.getAllItems(userId, { forceRefresh: true });
  if (import.meta.env.DEV) {
    console.log(`   ✓ Loaded ${items.length} items`);
    console.log('');
  }
  
  // Trigger suggestions refresh
  if (window.forceInsightsRefresh) {
    if (import.meta.env.DEV) {
      console.log('🔄 Refreshing Smart Insights...');
      console.log('   ✓ Triggered');
    }
    window.forceInsightsRefresh();
  }
  
  // Trigger memory surfacing refresh
  if (window.refreshMemorySurfacing) {
    if (import.meta.env.DEV) console.log('🔄 Refreshing Memory Surfacing...');
    await window.refreshMemorySurfacing();
    if (import.meta.env.DEV) console.log('   ✓ Triggered');
  }
  
  if (import.meta.env.DEV) {
    console.log('');
    console.log('✅ All insights refreshed');
    console.log('   Reload the page to see updated insights');
    console.log('');
    console.log('═══════════════════════════════════════════');
    console.log('');
  }
}

/**
 * Initialize debug tools
 */
export function initializeVaultDebug() {
  const shouldExpose = shouldExposeDebugTools();
  
  if (!shouldExpose) {
    // Check user ID after auth
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user && ALLOWED_USER_IDS.includes(user.id)) {
        exposeDebugTools();
      }
    });
  } else {
    exposeDebugTools();
  }
}

/**
 * Expose debug tools on window
 */
function exposeDebugTools() {
  window.backfillVault = backfillVault;
  window.validateVault = validateVault;
  window.testVaultPipeline = testVaultPipeline;
  window.invalidateInsightsCache = invalidateInsightsCache;
  window.forceInsightsRefresh = forceInsightsRefresh;
  
  // Expose vaultService for direct access
  window.vaultService = vaultService;
  
  if (import.meta.env.DEV) {
    console.log('');
    console.log('═══════════════════════════════════════════');
    console.log('🔧 VAULT DEBUG TOOLS LOADED');
    console.log('═══════════════════════════════════════════');
    console.log('');
    console.log('Available commands:');
    console.log('  window.backfillVault()         - Enrich missing items');
    console.log('  window.validateVault()         - Check vault health');
    console.log('  window.testVaultPipeline()     - Test write→read flow');
    console.log('  window.invalidateInsightsCache() - Clear all caches');
    console.log('  window.forceInsightsRefresh()  - Refresh dashboard');
    console.log('  window.vaultService.getAllItems(userId) - Get all items');
    console.log('');
    console.log('═══════════════════════════════════════════');
    console.log('');
  }
}

// Auto-initialize
if (typeof window !== 'undefined') {
  initializeVaultDebug();
}

export default {
  backfillVault,
  validateVault,
  testVaultPipeline,
  invalidateInsightsCache,
  forceInsightsRefresh,
  initializeVaultDebug,
};

